import { normalizeSize } from '@theme/size-quiz-core';

const Theme = window.Theme || {};

function formatMoney(cents) {
  return Theme.formatMoney ? Theme.formatMoney(cents) : `$${(Number(cents) / 100).toFixed(2)}`;
}

export function bundleTotals(variants, { savingsType = 'fixed', savingsValue = 0, minItems = 2 } = {}) {
  const count = variants.length;
  const subtotal = variants.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
  const compare = variants.reduce(
    (sum, variant) => sum + Math.max(Number(variant.compare_at_price || 0), Number(variant.price || 0)),
    0
  );
  let total = subtotal;
  const qualifies = count >= minItems && count > 0;
  if (qualifies) {
    if (savingsType === 'percent') {
      total = Math.round((subtotal * (100 - Math.min(100, Math.max(0, savingsValue)))) / 100);
    } else {
      total = Math.max(0, subtotal - Math.round(savingsValue * 100));
    }
  }
  return { count, subtotal, compare, total, savings: Math.max(0, compare - total), qualifies };
}

class ProductBundle extends HTMLElement {
  connectedCallback() {
    if (window.Theme?.abOff?.('bundle')) return;
    if (this._ready) {
      document.addEventListener('variant:change', this.onVariantChange);
      document.addEventListener('sizequiz:select', this.onQuizSelect);
      return;
    }
    this._ready = true;
    this.subtotalEl = this.querySelector('[data-bundle-subtotal]');
    this.totalEl = this.querySelector('[data-bundle-total]');
    this.discountRow = this.querySelector('[data-bundle-discount-row]');
    this.discountEl = this.querySelector('[data-bundle-discount]');
    this.savingsEl = this.querySelector('[data-bundle-savings]');
    this.messageEl = this.querySelector('[data-bundle-message]');
    this.submit = this.querySelector('[data-bundle-submit]');
    this.options = {
      savingsType: this.dataset.savingsType || 'fixed',
      savingsValue: Number(this.dataset.savingsValue || 0),
      minItems: Number(this.dataset.minItems || 2),
    };

    this.rows = [...this.querySelectorAll('[data-bundle-row]')].map((element) => {
      let variants = [];
      let options = [];
      let plan = {};
      try {
        plan = JSON.parse(element.querySelector('[data-bundle-variants]')?.textContent || '{}');
        variants = plan.variants || [];
      } catch {
        variants = [];
      }
      try {
        options = JSON.parse(element.dataset.defaultOptions || '[]');
      } catch {
        options = variants[0]?.options ? [...variants[0].options] : [];
      }
      const row = {
        element,
        variants,
        plan: { requires_selling_plan: Boolean(plan.requires_selling_plan), selling_plan: plan.selling_plan },
        options,
        current: element.dataset.current === 'true',
        needsSize: element.dataset.needsSize === 'true',
        sizeIndex: Number(element.dataset.sizePosition || 0) - 1,
        check: element.querySelector('[data-bundle-check]'),
        radios: [...element.querySelectorAll('input[type="radio"]')],
        error: element.querySelector('[data-row-error]'),
        priceEl: element.querySelector('[data-row-price]'),
        compareEl: element.querySelector('[data-row-compare]'),
        userPicked: false,
      };
      row.check?.addEventListener('change', () => {
        this.clearRowError(row);
        this.recalc();
      });
      element.addEventListener('click', (event) => {
        if (!row.check || event.target.closest('a, input, label, button, fieldset, .product-bundle__details')) return;
        row.check.click();
      });
      row.radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          row.userPicked = true;
          if (row.check && !row.check.checked) row.check.checked = true;
          this.clearRowError(row);
          this.recalc();
        });
      });
      return row;
    });

    this.onVariantChange = (event) => this.syncFromMain(event.detail?.variant);
    this.onQuizSelect = (event) => this.applySize(event.detail?.size, { includeCurrent: false });
    document.addEventListener('variant:change', this.onVariantChange);
    document.addEventListener('sizequiz:select', this.onQuizSelect);
    this.submit?.addEventListener('click', () => this.addBundle());
    this.recalc();
    if (this.rows.some((row) => row.current) && window.customElements) {
      customElements.whenDefined('product-info').then(() => {
        const info = document.querySelector('product-info');
        if (info?.currentVariant) this.syncFromMain(info.currentVariant);
      });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariantChange);
    document.removeEventListener('sizequiz:select', this.onQuizSelect);
  }

  selectedSize(row) {
    return row.radios.find((radio) => radio.checked)?.value ?? null;
  }

  rowOptions(row) {
    const options = [...row.options];
    const size = this.selectedSize(row);
    if (row.sizeIndex >= 0 && size !== null) options[row.sizeIndex] = size;
    return options;
  }

  findVariant(row, options) {
    const key = options.join('|||');
    return row.variants.find((variant) => (variant.options || []).join('|||') === key) || null;
  }

  rowVariant(row) {
    if (row.variants.length === 1) return row.variants[0];
    return this.findVariant(row, this.rowOptions(row));
  }

  displayVariant(row) {
    return this.rowVariant(row) || this.findVariant(row, row.options) || row.variants.find((v) => v.available) || row.variants[0];
  }

  syncFromMain(variant) {
    if (!variant) return;
    const current = this.rows.find((row) => row.current && row.variants.some((v) => v.id === variant.id));
    if (!current) return;
    current.options = [...(variant.options || current.options)];
    if (current.sizeIndex >= 0) {
      const size = variant.options?.[current.sizeIndex];
      current.radios.forEach((radio) => {
        radio.checked = radio.value === size;
      });
      this.clearRowError(current);
      this.applySize(size, { includeCurrent: false });
    }
    this.recalc();
  }

  applySize(size, { includeCurrent = true } = {}) {
    if (!size) return;
    const target = normalizeSize(size);
    this.rows.forEach((row) => {
      if (row.userPicked || (row.current && !includeCurrent) || row.sizeIndex < 0) return;
      const radio = row.radios.find((input) => normalizeSize(input.value) === target && !input.disabled);
      if (!radio) return;
      radio.checked = true;
      this.clearRowError(row);
    });
    this.recalc();
  }

  updateRowAvailability(row) {
    if (row.sizeIndex < 0) return;
    row.radios.forEach((radio) => {
      const options = [...row.options];
      options[row.sizeIndex] = radio.value;
      const available = Boolean(this.findVariant(row, options)?.available);
      radio.disabled = !available;
      const label = row.element.querySelector(`label[for="${CSS.escape(radio.id)}"]`);
      label?.toggleAttribute('data-unavailable', !available);
      if (!available && radio.checked) radio.checked = false;
    });
  }

  clearRowError(row) {
    row.element.classList.remove('is-error');
    if (row.error) row.error.hidden = true;
  }

  recalc() {
    const chosen = [];
    this.rows.forEach((row) => {
      this.updateRowAvailability(row);
      const selected = Boolean(row.check?.checked);
      row.element.classList.toggle('is-selected', selected);
      const variant = this.displayVariant(row);
      if (variant) {
        const onSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        if (row.priceEl) {
          row.priceEl.textContent = formatMoney(variant.price);
          row.priceEl.classList.toggle('is-sale', onSale);
        }
        if (row.compareEl) {
          row.compareEl.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
          row.compareEl.hidden = !onSale;
        }
      }
      if (selected && variant) chosen.push(variant);
    });

    const totals = bundleTotals(chosen, this.options);
    const discount = Math.max(0, totals.subtotal - totals.total);
    if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(totals.subtotal);
    if (this.totalEl) this.totalEl.textContent = formatMoney(totals.total);
    if (this.discountEl) this.discountEl.textContent = discount > 0 ? `-${formatMoney(discount)}` : '';
    if (this.discountRow) this.discountRow.hidden = discount <= 0;
    if (this.savingsEl) {
      let text = '';
      let hint = false;
      if (totals.qualifies && totals.savings > 0) {
        text = (this.savingsEl.dataset.template || '').replace('[amount]', formatMoney(totals.savings));
      } else if (!totals.qualifies && this.savingsEl.dataset.hint && this.rows.length >= this.options.minItems) {
        text = this.savingsEl.dataset.hint.replace('[count]', String(this.options.minItems));
        hint = true;
      }
      this.savingsEl.textContent = text;
      this.savingsEl.classList.toggle('is-hint', hint);
      this.savingsEl.hidden = !text;
    }
    if (this.submit) this.submit.disabled = totals.count === 0;
    if (this.messageEl && !this.messageEl.hidden && !this.rows.some((row) => row.element.classList.contains('is-error'))) {
      this.messageEl.hidden = true;
    }
    return totals;
  }

  addBundle() {
    if (this._busy) return;
    const items = [];
    const invalid = [];
    const bundleId = `${this.dataset.productId || this.dataset.sectionId}-${Date.now().toString(36)}`;

    this.rows.forEach((row) => {
      if (!row.check?.checked) return;
      const hasSize = !row.needsSize || this.selectedSize(row) !== null;
      const variant = hasSize ? this.rowVariant(row) : null;
      if (!variant?.id || !variant.available) {
        invalid.push(row);
        row.element.classList.add('is-error');
        if (row.error) row.error.hidden = false;
        return;
      }
      items.push(Theme.withSellingPlan({ id: variant.id, quantity: 1, properties: { _bundle: bundleId } }, row.plan));
    });

    if (invalid.length) {
      if (this.messageEl) {
        this.messageEl.textContent = this.messageEl.dataset.error || '';
        this.messageEl.hidden = false;
      }
      this.classList.remove('is-shake');
      void this.offsetWidth;
      this.classList.add('is-shake');
      setTimeout(() => this.classList.remove('is-shake'), 500);
      invalid[0].radios.find((radio) => !radio.disabled)?.focus();
      return;
    }

    if (!items.length || !Theme.cart?.add) return;
    if (this.messageEl) this.messageEl.hidden = true;
    this._busy = true;
    this.submit?.setAttribute('aria-busy', 'true');
    Theme.cart
      .add(items, { open: true, source: 'bundle', errorToast: !this.messageEl })
      .then(() => Theme.ss?.('feat', 'bundle_add', { items: items.length, qty: items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0) }))
      .catch((error) => {
        if (!this.messageEl) return;
        this.messageEl.textContent = error?.message || Theme.strings?.error || '';
        this.messageEl.hidden = false;
      })
      .finally(() => {
        this._busy = false;
        this.submit?.removeAttribute('aria-busy');
      });
  }
}

if (!customElements.get('product-bundle')) customElements.define('product-bundle', ProductBundle);
